import { Field } from 'formik';
import {
  Box,
  Grid,
  GridItem,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Input,
} from '@chakra-ui/react';
import { VTOTextField } from './VTOTextField';
import { VTOArrayField } from './VTOArrayField';
import { NumericTargetInput } from './NumericTargetInput';

interface TractionViewProps {
  isEditing: boolean;
  onCreateRocksClick: () => void;
  isPrinting?: boolean;
}

/**
 * Traction View Component
 * Displays and allows editing of the Traction section of the V/TO
 * Migrated from Tailwind to Chakra UI, uses Formik Field components
 */
export function TractionView({ isEditing, onCreateRocksClick, isPrinting = false }: TractionViewProps) {
  return (
    <Box maxW="5xl" mx="auto">
      <Box borderWidth="2px" borderColor="gray.900" minH="800px">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderBottomWidth="2px"
          borderColor="gray.900"
          bg="gray.100"
          py={3}
        >
          <Heading size="lg" color="gray.900" letterSpacing="wide">
            TRACTION
          </Heading>
        </Box>

        <Grid 
          templateColumns={isPrinting ? "repeat(2, 1fr)" : { base: "1fr", md: "repeat(2, 1fr)" }} 
          minH="760px"
        >
          <GridItem
            borderRightWidth={isPrinting ? "2px" : { md: "2px" }}
            borderColor="gray.900"
            p={{ base: 4, md: 6 }}
          >
            <Box>
              <Box
                display="flex"
                alignItems="center"
                borderBottomWidth="2px"
                borderColor="sky.500"
                pb={isPrinting ? 3 : 1}
                mb={isPrinting ? 4 : 3}
              >
                <Heading size="xs" color="sky.700" textTransform="uppercase" letterSpacing="wider">
                  1-Year Plan
                </Heading>
              </Box>
              <VStack spacing={2} align="stretch" mb={4}>
                <HStack spacing={2} align="center" fontSize="xs">
                  <Text fontWeight="semibold" color="gray.600" w={isPrinting ? 'auto' : 24} mr={isPrinting ? 4 : 0}>
                    Future Date:
                  </Text>
                  <Box flex={1}>
                    <Field
                      name="oneYearDate"
                      component={VTOTextField}
                      placeholder="e.g., December 31, 2025"
                      rows={1}
                      isEditing={isEditing}
                      notDefinedText="Not set"
                    />
                  </Box>
                </HStack>
                <HStack spacing={2} align="center" fontSize="xs">
                  <Text fontWeight="semibold" color="gray.600" w={isPrinting ? 'auto' : 24} mr={isPrinting ? 4 : 0}>
                    Revenue:
                  </Text>
                  <Box flex={1}>
                    <Field
                      name="oneYearRevenueTarget"
                      component={NumericTargetInput}
                      placeholder="e.g., 20M"
                      isEditing={isEditing}
                      notDefinedText="Not set"
                    />
                  </Box>
                </HStack>
                <HStack spacing={2} align="center" fontSize="xs">
                  <Text fontWeight="semibold" color="gray.600" w={isPrinting ? 'auto' : 24} mr={isPrinting ? 4 : 0}>
                    Profit:
                  </Text>
                  <Box flex={1}>
                    <Field
                      name="oneYearProfitTarget"
                      component={NumericTargetInput}
                      placeholder="e.g., 3M"
                      isEditing={isEditing}
                      notDefinedText="Not set"
                    />
                  </Box>
                </HStack>
                {isEditing && (
                  <Box bg="orange.50" p={3} rounded="md">
                    <Text fontSize="xs" fontWeight="semibold" color="orange.700" mb={2}>
                      Reviews Targets
                    </Text>
                    <VStack spacing={2} align="stretch">
                      <HStack spacing={2} align="center">
                        <Text fontSize="xs" color="orange.700" flex={1}>
                          Forecasted Loans
                        </Text>
                        <Field name="oneYearForecastedLoans">
                          {({ field, form }: any) => (
                            <Input
                              type="number"
                              size="sm"
                              width="120px"
                              value={field.value ?? 0}
                              onChange={(e) =>
                                form.setFieldValue('oneYearForecastedLoans', e.target.value === '' ? 0 : Number(e.target.value))
                              }
                              borderColor="orange.300"
                            />
                          )}
                        </Field>
                      </HStack>
                      <HStack spacing={2} align="center">
                        <Text fontSize="xs" color="orange.700" flex={1}>
                          Review Target %
                        </Text>
                        <Field name="oneYearReviewTargetPct">
                          {({ field, form }: any) => (
                            <Input
                              type="number"
                              size="sm"
                              width="120px"
                              value={field.value ?? 0}
                              onChange={(e) =>
                                form.setFieldValue('oneYearReviewTargetPct', e.target.value === '' ? 0 : Number(e.target.value))
                              }
                              borderColor="orange.300"
                            />
                          )}
                        </Field>
                      </HStack>
                    </VStack>
                  </Box>
                )}
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
                    Measurables
                  </Text>
                  <Field
                    name="oneYearMeasurables"
                    component={VTOArrayField}
                    placeholder="Key metric"
                    addButtonText="Add Measurable"
                    isEditing={isEditing}
                  />
                </Box>
              </VStack>
              <Box>
                <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
                  Goals for the Year
                </Text>
                <Field
                  name="oneYearGoals"
                  component={VTOArrayField}
                  placeholder="Annual goal"
                  maxItems={7}
                  addButtonText="Add Goal"
                  isEditing={isEditing}
                />
              </Box>
            </Box>
          </GridItem>

          <GridItem p={6}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  borderBottomWidth="2px"
                  borderColor="amber.500"
                  pb={isPrinting ? 3 : 1}
                  mb={isPrinting ? 4 : 3}
                >
                  <Heading size="xs" color="amber.700" textTransform="uppercase" letterSpacing="wider">
                    Rocks
                  </Heading>
                </Box>
                <VStack spacing={4} align="stretch" mb={4}>
                  <HStack spacing={2} align="center" fontSize="xs">
                    <Text fontWeight="semibold" color="gray.600" w={isPrinting ? 'auto' : 24} mr={isPrinting ? 4 : 0}>
                      Future Date:
                    </Text>
                    <Box flex={1}>
                      <Field
                        name="rocksDate"
                        component={VTOTextField}
                        placeholder="e.g., March 31, 2025"
                        rows={1}
                        isEditing={isEditing}
                        notDefinedText="Not set"
                      />
                    </Box>
                  </HStack>
                  <HStack spacing={2} align="center" fontSize="xs">
                    <Text fontWeight="semibold" color="gray.600" w={isPrinting ? 'auto' : 24} mr={isPrinting ? 4 : 0}>
                      Revenue:
                    </Text>
                    <Box flex={1}>
                      <Field
                        name="rocksRevenueTarget"
                        component={NumericTargetInput}
                        placeholder="e.g., 5M"
                        isEditing={isEditing}
                        notDefinedText="Not set"
                      />
                    </Box>
                  </HStack>
                  <HStack spacing={2} align="center" fontSize="xs">
                    <Text fontWeight="semibold" color="gray.600" w={isPrinting ? 'auto' : 24} mr={isPrinting ? 4 : 0}>
                      Profit:
                    </Text>
                    <Box flex={1}>
                      <Field
                        name="rocksProfitTarget"
                        component={NumericTargetInput}
                        placeholder="e.g., 1M"
                        isEditing={isEditing}
                        notDefinedText="Not set"
                      />
                    </Box>
                  </HStack>
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
                      Measurables
                    </Text>
                    <Field
                      name="rocksMeasurables"
                      component={VTOArrayField}
                      placeholder="Key metric"
                      addButtonText="Add Measurable"
                      isEditing={isEditing}
                    />
                  </Box>
                </VStack>
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                      Rocks for the Quarter
                    </Text>
                    {!isEditing && !isPrinting && (
                      <Field name="rocksList">
                        {({ field }: any) => (
                          (field.value || []).length > 0 && (
                            <Button
                              size="xs"
                              colorScheme="green"
                              onClick={onCreateRocksClick}
                            >
                              Create Rocks from V/TO
                            </Button>
                          )
                        )}
                      </Field>
                    )}
                  </HStack>
                  <Field
                    name="rocksList"
                    component={VTOArrayField}
                    placeholder="Rock description"
                    addButtonText="Add Rock"
                    isEditing={isEditing}
                  />
                </Box>
              </Box>

              <Box>
                <HStack justify="space-between" borderBottomWidth="2px" borderColor="red.500" pb={isPrinting ? 3 : 1} mb={isPrinting ? 4 : 3}>
                  <Heading size="xs" color="red.700" textTransform="uppercase" letterSpacing="wider">
                    Issues List
                  </Heading>
                  <Text fontSize="xs" color="gray.500" fontStyle="italic">
                    (Identify / Discuss / Solve)
                  </Text>
                </HStack>
                <Field
                  name="issuesList"
                  component={VTOArrayField}
                  placeholder="Issue description"
                  addButtonText="Add Issue"
                  isEditing={isEditing}
                />
              </Box>
            </VStack>
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
}








